export interface SystemContentItem {
    key: string;
    label: string;
    description?: string;
    defaultValue?: string; // Default English Value
}

export interface SystemContentSection {
    id: string;
    title: string;
    description?: string;
    group?: 'Header & Menu' | 'Footer' | 'Pages' | 'Common' | 'Other';
    required?: boolean;
    items: SystemContentItem[];
}

export const systemContentConfig: SystemContentSection[] = [
    {
        id: 'navigation',
        title: 'Navigation',
        description: 'Main menu and header items',
        group: 'Header & Menu',
        required: true,
        items: [
            { key: 'nav.home', label: 'Home', defaultValue: 'Home' },
            { key: 'nav.about', label: 'About Us', defaultValue: 'About Us' },
            { key: 'nav.tours', label: 'Tours', defaultValue: 'Tours' },
            { key: 'nav.packages', label: 'Packages', defaultValue: 'Packages' },
            { key: 'nav.shoreExcursions', label: 'Shore Excursions', defaultValue: 'Shore Excursions' },
            { key: 'header.explore', label: 'Explore Button', defaultValue: 'Explore' },
            { key: 'destination_card.explore', label: 'Destination Card Explore', defaultValue: 'Explore Tours' },
        ]
    },
    {
        id: 'tours_page',
        title: 'Tours Page',
        description: 'Titles and labels for the Tours page',
        group: 'Pages',
        items: [
            { key: 'tours_page_title', label: 'Main Title', defaultValue: 'Explore Our Tours' },
            { key: 'tours_page_subtitle', label: 'Subtitle', defaultValue: 'Discover the beauty of Tunisia.' },
        ]
    },
    {
        id: 'explore_page',
        title: 'Explore Page',
        description: 'Titles and labels for the Explore page',
        group: 'Pages',
        items: [
            { key: 'explore.page.title', label: 'Main Title', defaultValue: 'Explore Tunisia' },
            { key: 'explore.page.subtitle', label: 'Subtitle', defaultValue: 'Discover amazing packages, tours, and experiences across Tunisia' },
        ]
    },
    {
        id: 'packages_page',
        title: 'Packages Page',
        description: 'Titles and labels for the Packages page',
        group: 'Pages',
        items: [
            { key: 'packages_page_title', label: 'Main Title', defaultValue: 'Explore Our Packages' },
            { key: 'packages_page_subtitle', label: 'Subtitle', defaultValue: 'Discover amazing packages.' },
        ]
    },
    {
        id: 'shore_excursions_page',
        title: 'Shore Excursions Page',
        description: 'Titles and labels for the Shore Excursions page',
        group: 'Pages',
        items: [
            { key: 'shore_excursions_page_title', label: 'Main Title', defaultValue: 'Shore Excursions' },
            { key: 'shore_excursions_page_subtitle', label: 'Subtitle', defaultValue: 'Discover our curated shore excursions for cruise passengers.' },
        ]
    },
    {
        id: 'partner_logos',
        title: 'Partner Logos',
        description: 'Labels for the partner trust logos bar',
        group: 'Common',
        items: [
            { key: 'partners.google', label: 'Google Reviews Label', defaultValue: 'Google' },
            { key: 'partners.iso', label: 'ISO Label', defaultValue: 'Certified' },
            { key: 'partners.feefo', label: 'Feefo Label', defaultValue: 'Feefo' },
            { key: 'partners.tripadvisor', label: 'TripAdvisor Label', defaultValue: 'TripAdvisor' },
        ]
    },
    {
        id: 'tours_destinations',
        title: 'Tours Destinations',
        description: 'Labels for destination filtering on Tours page',
        group: 'Pages',
        items: [
            { key: 'tours.destinations.title', label: 'Section Title', defaultValue: 'Explore by Destination' },
            { key: 'tours.destinations.all', label: 'All Button', defaultValue: 'All' },
        ]
    },
    {
        id: 'destinations_page',
        title: 'Destinations Page',
        description: 'Titles and labels for the Destinations page',
        group: 'Pages',
        items: [
            { key: 'destinations_page_title', label: 'Main Title', defaultValue: 'Top Destinations' },
            { key: 'destinations_page_subtitle', label: 'Subtitle', defaultValue: 'Explore our most popular destinations.' },
        ]
    },
    {
        id: 'homepage',
        title: 'Homepage Sections',
        description: 'Section titles and content on the homepage',
        group: 'Pages',
        required: true,
        items: [
            { key: 'homepage.topPackages.title', label: 'Top Packages Title', defaultValue: 'Top Packages' },
            { key: 'homepage.topPackages.subtitle', label: 'Top Packages Subtitle', defaultValue: 'Discover our best travel packages' },
            { key: 'homepage.topPackages.seeMore', label: 'Top Packages See More Button', defaultValue: 'See More' },
            { key: 'homepage.dayTours.title', label: 'Day Tours Title', defaultValue: 'Day Tours' },
            { key: 'homepage.blog.title', label: 'Blog Section Title', defaultValue: 'Latest Travel Inspiration' },
            { key: 'homepage.blog.subtitle', label: 'Blog Section Subtitle', defaultValue: 'Stories, guides, and tips for your journey.' },
            { key: 'homepage.blog.viewAllPosts', label: 'Blog View All Button (desktop)', defaultValue: 'View all posts' },
            { key: 'homepage.blog.viewAllArticles', label: 'Blog View All Button (mobile)', defaultValue: 'View all articles' },
            { key: 'homepage.blog.readMore', label: 'Blog Read More Button', defaultValue: 'Read Article' },
        ]
    },
    {
        id: 'search_filter',
        title: 'Search Filter',
        description: 'Search and filter form labels',
        group: 'Common',
        items: [
            { key: 'search.destination.label', label: 'Destination Label', defaultValue: 'Destination' },
            { key: 'search.destination.placeholder', label: 'Destination Placeholder', defaultValue: 'Where are you going?' },
            { key: 'search.checkin.label', label: 'Check-in Label', defaultValue: 'Check-in' },
            { key: 'search.checkin.placeholder', label: 'Check-in Placeholder', defaultValue: 'dd/mm/yyyy' },
            { key: 'search.checkout.label', label: 'Check-out Label', defaultValue: 'Check-out' },
            { key: 'search.checkout.placeholder', label: 'Check-out Placeholder', defaultValue: 'dd/mm/yyyy' },
            { key: 'search.guests.label', label: 'Guests Label', defaultValue: 'Guests' },
            { key: 'search.guests.placeholder', label: 'Guests Placeholder', defaultValue: '2 guests' },
            { key: 'search.button', label: 'Search Button', defaultValue: 'Search' },
        ]
    },
    {
        id: 'user_menu',
        title: 'User Menu',
        description: 'User dropdown menu items',
        group: 'Header & Menu',
        items: [
            { key: 'user.menu.profile', label: 'Profile', defaultValue: 'Profile' },
            { key: 'user.menu.bookings', label: 'Bookings', defaultValue: 'Bookings' },
            { key: 'user.menu.settings', label: 'Settings', defaultValue: 'Settings' },
            { key: 'user.menu.logout', label: 'Logout', defaultValue: 'Logout' },
        ]
    },
    {
        id: 'footer_general',
        title: 'Footer',
        description: 'Global footer content',
        group: 'Footer',
        required: true,
        items: [
            { key: 'footer.tagline', label: 'Tagline', defaultValue: 'Experience the magic of Tunisia with us.' },
            { key: 'footer.copyright', label: 'Copyright', defaultValue: '© 2026 TunisTravels. All rights reserved.' },
            { key: 'footer.weAccept', label: 'We Accept', defaultValue: 'We Accept' },
            { key: 'footer.travellers.title', label: 'Travellers Title', defaultValue: 'Travellers' },
            { key: 'footer.travellers.whyTravellers', label: 'Travellers: Why', defaultValue: 'Why Travellers' },
            { key: 'footer.travellers.customer', label: 'Travellers: Stories', defaultValue: 'Customer Stories' },
            { key: 'footer.company.title', label: 'Company Title', defaultValue: 'Company' },
            { key: 'footer.company.aboutUs', label: 'Company: About', defaultValue: 'About Us' },
            { key: 'footer.company.logIn', label: 'Company: Login', defaultValue: 'Log In' },
            { key: 'footer.getApp.title', label: 'Get App Title', defaultValue: 'Get App' },
            { key: 'footer.getApp.appStore', label: 'Get App: App Store', defaultValue: 'App Store' },
            { key: 'footer.getApp.playStore', label: 'Get App: Play Store', defaultValue: 'Google Play' },
        ]
    },
    {
        id: 'about_page',
        title: 'About Page',
        description: 'Content for the About Us page',
        group: 'Pages',
        required: true,
        items: [
            // Hero
            { key: 'about.hero.title', label: 'Hero Title', defaultValue: 'Your Journey,\nOur Passion' },
            { key: 'about.hero.subtitle', label: 'Hero Subtitle', defaultValue: "For over 15 years, we've been crafting unforgettable travel experiences that connect you with the heart and soul of Tunisia." },
            { key: 'about.cta.button', label: 'Hero CTA Button', defaultValue: 'Start Your Journey' },

            // Stats
            { key: 'about.stats.years', label: 'Stats: Years', defaultValue: '15+' },
            { key: 'about.stats.years.label', label: 'Stats: Years Label', defaultValue: 'Years of Excellence' },
            { key: 'about.stats.tours', label: 'Stats: Tours', defaultValue: '5000+' },
            { key: 'about.stats.tours.label', label: 'Stats: Tours Label', defaultValue: 'Successful Tours' },
            { key: 'about.stats.travelers', label: 'Stats: Travelers', defaultValue: '12k+' },
            { key: 'about.stats.travelers.label', label: 'Stats: Travelers Label', defaultValue: 'Happy Travelers' },
            { key: 'about.stats.satisfaction', label: 'Stats: Satisfaction', defaultValue: '98%' },
            { key: 'about.stats.satisfaction.label', label: 'Stats: Satisfaction Label', defaultValue: 'Client Satisfaction' },

            // Story
            { key: 'about.story.title', label: 'Story Title', defaultValue: 'Our Story' },
            { key: 'about.story.subtitle', label: 'Story Subtitle', defaultValue: 'From humble beginnings to Tunisia\'s leading travel partner.' },
            { key: 'about.story.content', label: 'Story Content', defaultValue: "Founded in 2010 by a group of passionate local guides, TunisTravels began with a simple mission: to show the world the true beauty of Tunisia beyond the typical tourist trails.\n\nWhat started as a small operation offering personalized desert excursions has grown into a full-service travel agency. We believe that travel is not just about seeing new places, but about feeling them. That's why every tour we design is infused with authentic local experiences, from sharing bread with Berber families to exploring ancient Roman ruins with expert historians.\n\nToday, we are proud to be the trusted partner for travelers seeking authentic, safe, and unforgettable Tunisian adventures." },

            // Mission
            { key: 'about.mission.title', label: 'Mission Title', defaultValue: 'Our Mission' },
            { key: 'about.mission.desc', label: 'Mission Description', defaultValue: 'To bridge cultures through immersive travel experiences that honor Tunisian heritage while promoting sustainable and responsible tourism.' },

            // Vision
            { key: 'about.vision.title', label: 'Vision Title', defaultValue: 'Our Vision' },
            { key: 'about.vision.desc', label: 'Vision Description', defaultValue: 'To be the global reference for authentic North African travel, setting the standard for quality, innovation, and cultural integrity.' },

            // Values
            { key: 'about.values.title', label: 'Values Title', defaultValue: 'Our Core Values' },
            { key: 'about.values.subtitle', label: 'Values Subtitle', defaultValue: 'The principles that guide every journey we craft.' },
            { key: 'about.values.passion.title', label: 'Value: Passion Title', defaultValue: 'Deep Passion' },
            { key: 'about.values.passion.desc', label: 'Value: Passion Desc', defaultValue: 'We don\'t just sell tours; we share our love for our homeland.' },
            { key: 'about.values.integrity.title', label: 'Value: Integrity Title', defaultValue: 'Unwavering Integrity' },
            { key: 'about.values.integrity.desc', label: 'Value: Integrity Desc', defaultValue: 'Honesty and transparency are the foundations of our relationships.' },
            { key: 'about.values.innovation.title', label: 'Value: Innovation Title', defaultValue: 'Constant Innovation' },
            { key: 'about.values.innovation.desc', label: 'Value: Innovation Desc', defaultValue: 'We continuously seek new ways to surprise and delight our guests.' },
            { key: 'about.values.sustainability.title', label: 'Value: Sustainability Title', defaultValue: 'Sustainable Impact' },
            { key: 'about.values.sustainability.desc', label: 'Value: Sustainability Desc', defaultValue: 'We are committed to preserving our environment and supporting local communities.' },

            // Timeline
            { key: 'about.timeline.title', label: 'Timeline Title', defaultValue: 'Our Journey' },
            { key: 'about.timeline.subtitle', label: 'Timeline Subtitle', defaultValue: 'Key milestones that shaped who we are today.' },

            // Contact
            { key: 'about.contact.title', label: 'Contact Title', defaultValue: 'Get in Touch' },
            { key: 'about.contact.subtitle', label: 'Contact Subtitle', defaultValue: 'Ready to plan your Tunisian adventure? We\'re here to help.' },
            { key: 'about.contact.email', label: 'Contact Email', defaultValue: 'hello@tunistravels.com' },
            { key: 'about.contact.phone', label: 'Contact Phone', defaultValue: '+216 71 123 456' },
            { key: 'about.contact.address', label: 'Contact Address', defaultValue: '123 Avenue Habib Bourguiba, Tunis, Tunisia' },
            { key: 'about.contact.button', label: 'Contact Button', defaultValue: 'Send Message' },

            // Why Choose Us
            { key: 'about.why.title', label: 'Why Title', defaultValue: 'Why Choose Us' },
            { key: 'about.why.subtitle', label: 'Why Subtitle', defaultValue: 'We go the extra mile to make your trip perfect.' },
            { key: 'about.why.expertise.title', label: 'Why: Expertise Title', defaultValue: 'Local Expertise' },
            { key: 'about.why.expertise.desc', label: 'Why: Expertise Desc', defaultValue: 'Our guides are certified experts with deep local knowledge.' },
            { key: 'about.why.customization.title', label: 'Why: Customization Title', defaultValue: 'Tailored Journeys' },
            { key: 'about.why.customization.desc', label: 'Why: Customization Desc', defaultValue: 'Every itinerary is customized to your preferences and pace.' },
            { key: 'about.why.support.title', label: 'Why: Support Title', defaultValue: '24/7 Support' },
            { key: 'about.why.support.desc', label: 'Why: Support Desc', defaultValue: 'We are always just a phone call away, anytime, anywhere.' },
            { key: 'about.why.value.title', label: 'Why: Value Title', defaultValue: 'Best Value' },
            { key: 'about.why.value.desc', label: 'Why: Value Desc', defaultValue: 'Premium experiences at competitive, transparent prices.' },
        ]
    },

    {
        id: 'account_pages',
        title: 'Account Pages',
        description: 'Labels for the User Account sections (Profile, Bookings, Support, Reviews, Settings)',
        group: 'Pages',
        items: [
            // Profile
            { key: 'profile.title', label: 'Profile Title', defaultValue: 'Profile' },
            { key: 'profile.subtitle', label: 'Profile Subtitle', defaultValue: 'Manage your personal information' },
            { key: 'profile.button.edit', label: 'Button: Edit', defaultValue: 'Edit' },
            { key: 'profile.button.cancel', label: 'Button: Cancel', defaultValue: 'Cancel' },
            { key: 'profile.button.save', label: 'Button: Save', defaultValue: 'Save Changes' },
            { key: 'profile.button.saving', label: 'Button: Saving', defaultValue: 'Saving...' },
            { key: 'profile.error', label: 'Profile Error', defaultValue: 'Failed to update profile.' },
            { key: 'profile.label.fullName', label: 'Label: Full Name', defaultValue: 'Full Name' },
            { key: 'profile.placeholder.name', label: 'Placeholder: Name', defaultValue: 'Enter your name' },
            { key: 'profile.label.email', label: 'Label: Email', defaultValue: 'Email Address' },
            { key: 'profile.placeholder.email', label: 'Placeholder: Email', defaultValue: 'Enter your email' },
            { key: 'profile.email_readonly', label: 'Email Readonly Note', defaultValue: 'Email cannot be changed' },
            { key: 'profile.label.phone', label: 'Label: Phone', defaultValue: 'Phone Number' },
            { key: 'profile.label.country', label: 'Label: Country', defaultValue: 'Country' },

            // Bookings
            { key: 'account.bookings.title', label: 'Title', defaultValue: 'My Bookings' },
            { key: 'account.bookings.bookNew', label: 'Book New Trip', defaultValue: 'Book New Trip' },
            { key: 'account.bookings.noBookings', label: 'No Bookings', defaultValue: 'No bookings found' },
            { key: 'account.bookings.filter.all', label: 'Filter: All', defaultValue: 'All' },
            { key: 'account.bookings.filter.upcoming', label: 'Filter: Upcoming', defaultValue: 'Upcoming' },
            { key: 'account.bookings.filter.completed', label: 'Filter: Completed', defaultValue: 'Completed' },
            { key: 'account.bookings.filter.cancelled', label: 'Filter: Cancelled', defaultValue: 'Cancelled' },
            { key: 'account.bookings.table.date', label: 'Table: Date', defaultValue: 'Date' },
            { key: 'account.bookings.table.guests', label: 'Table: Guests', defaultValue: 'Guests' },
            { key: 'account.bookings.table.total', label: 'Table: Total', defaultValue: 'Total' },
            { key: 'account.bookings.viewDetails', label: 'View Details', defaultValue: 'View Details' },
            
            // Support
            { key: 'account.support.title', label: 'Title', defaultValue: 'My Support Tickets' },
            { key: 'account.support.newTicket', label: 'New Ticket', defaultValue: 'New Ticket' },
            { key: 'account.support.search', label: 'Search', defaultValue: 'Search tickets...' },
            { key: 'account.support.noTickets.title', label: 'No Tickets Title', defaultValue: 'No support tickets' },
            { key: 'account.support.noTickets.desc', label: 'No Tickets Desc', defaultValue: "You haven't submitted any support tickets yet. Need help with your booking?" },
            { key: 'account.support.contactSupport', label: 'Contact Support', defaultValue: 'Contact Support' },
            { key: 'account.support.status.open', label: 'Status: Open', defaultValue: 'Open' },
            { key: 'account.support.status.answered', label: 'Status: Answered', defaultValue: 'Answered' },
            { key: 'account.support.status.closed', label: 'Status: Closed', defaultValue: 'Closed' },
            
            // Reviews
            { key: 'account.reviews.title', label: 'Title', defaultValue: 'My Reviews' },
            { key: 'account.reviews.noReviews.title', label: 'No Reviews Title', defaultValue: 'No reviews yet' },
            { key: 'account.reviews.noReviews.desc', label: 'No Reviews Desc', defaultValue: "You haven't written any reviews yet. After you complete a tour, you can share your experience here!" },
            { key: 'account.reviews.exploreTours', label: 'Explore Tours', defaultValue: 'Explore Tours' },
            { key: 'account.reviews.status.published', label: 'Status: Published', defaultValue: 'Published' },
            { key: 'account.reviews.status.pending', label: 'Status: Pending', defaultValue: 'Pending Approval' },
            { key: 'account.reviews.status.rejected', label: 'Status: Rejected', defaultValue: 'Rejected' },
            { key: 'account.reviews.reviewed', label: 'Reviewed Label', defaultValue: 'Reviewed: ' },

            // Settings
            { key: 'account.settings.title', label: 'Title', defaultValue: 'Settings' },
            { key: 'account.settings.notifications', label: 'Notifications', defaultValue: 'Notifications' },
            { key: 'account.settings.email.title', label: 'Email Notifications', defaultValue: 'Email Notifications' },
            { key: 'account.settings.email.desc', label: 'Email Desc', defaultValue: 'Receive booking updates via email' },
            { key: 'account.settings.sms.title', label: 'SMS Notifications', defaultValue: 'SMS Notifications' },
            { key: 'account.settings.sms.desc', label: 'SMS Desc', defaultValue: 'Receive booking updates via SMS' },
            { key: 'account.settings.marketing.title', label: 'Marketing Emails', defaultValue: 'Marketing Emails' },
            { key: 'account.settings.marketing.desc', label: 'Marketing Desc', defaultValue: 'Receive promotions and offers' },
            { key: 'account.settings.preferences', label: 'Preferences', defaultValue: 'Preferences' },
            { key: 'account.settings.language', label: 'Language', defaultValue: 'Language' },
            { key: 'account.settings.currency', label: 'Currency', defaultValue: 'Currency' },
            { key: 'account.settings.security', label: 'Security', defaultValue: 'Security' },
            { key: 'account.settings.2fa.title', label: '2FA Title', defaultValue: 'Two-Factor Authentication' },
            { key: 'account.settings.2fa.desc', label: '2FA Desc', defaultValue: 'Add an extra layer of security' },
            { key: 'account.settings.changePassword', label: 'Change Password', defaultValue: 'Change Password' },
            { key: 'account.settings.dangerZone', label: 'Danger Zone', defaultValue: 'Danger Zone' },
            { key: 'account.settings.deleteWarning', label: 'Delete Warning', defaultValue: 'Once you delete your account, there is no going back. Please be certain.' },
            { key: 'account.settings.deleteAccount', label: 'Delete Account', defaultValue: 'Delete Account' },
        ]
    },
    {
        id: 'favorites_page',
        title: 'Favorites Page',
        description: 'Labels for the User Favorites page',
        group: 'Pages',
        items: [
            { key: 'favorites.title', label: 'Page Title', defaultValue: 'My Favorites' },
            { key: 'favorites.subtitle', label: 'Page Subtitle', defaultValue: 'Your saved tours and packages' },
            { key: 'favorites.empty', label: 'Empty State', defaultValue: 'You haven\'t saved any favorites yet.' },
        ]
    },
    {
        id: 'account_sidebar',
        title: 'Account Sidebar',
        description: 'Navigation items for the account sidebar',
        group: 'Pages',
        items: [
            { key: 'account.nav.profile', label: 'Nav: Profile', defaultValue: 'Personal Data' },
            { key: 'account.nav.bookings', label: 'Nav: Bookings', defaultValue: 'My Bookings' },
            { key: 'account.nav.favorites', label: 'Nav: Favorites', defaultValue: 'My Favorites' },
            { key: 'account.nav.reviews', label: 'Nav: Reviews', defaultValue: 'My Reviews' },
            { key: 'account.nav.support', label: 'Nav: Support', defaultValue: 'Support' },
            { key: 'account.nav.settings', label: 'Nav: Settings', defaultValue: 'Settings' },
            { key: 'account.nav.security', label: 'Nav: Security', defaultValue: 'Security' },
            { key: 'account.nav.notifications', label: 'Nav: Notifications', defaultValue: 'Notifications' },
            { key: 'account.nav.payment', label: 'Nav: Payment', defaultValue: 'Payment Account' },
            { key: 'account.nav.logout', label: 'Nav: Logout', defaultValue: 'Log out' },
        ]
    },
    {
        id: 'gallery_section',
        title: 'Gallery Section',
        description: 'Title and subtitle for the gallery section',
        group: 'Common',
        items: [
            { key: 'gallery.title', label: 'Gallery Title', defaultValue: 'Our Gallery' },
            { key: 'gallery.subtitle', label: 'Gallery Subtitle', defaultValue: 'Explore the beauty of Tunisia through our curated collection of stunning destinations' },
            { key: 'gallery.swipe_hint', label: 'Swipe Hint (Mobile)', defaultValue: 'Swipe to see more →' },
        ]
    },
    {
        id: 'testimonials_section',
        title: 'Testimonials Section',
        description: 'Section title and labels for the testimonials carousel',
        group: 'Common',
        items: [
            { key: 'testimonials.title', label: 'Section Title', defaultValue: 'What Our Travelers Say' },
            { key: 'testimonials.verified_traveler', label: 'Verified Label', defaultValue: 'Verified Traveler via' },
        ]
    },
    {
        id: 'blog_page',
        title: 'Blog Page',
        description: 'Titles and labels for the Blog listing page',
        group: 'Pages',
        items: [
            { key: 'blog.page.title', label: 'Page Title', defaultValue: 'Travel Blog' },
            { key: 'blog.page.subtitle', label: 'Page Subtitle', defaultValue: 'Inspiring stories, travel guides, and tips for your Tunisian adventure.' },
            { key: 'blog.page.readMore', label: 'Read More Button', defaultValue: 'Read More' },
            { key: 'blog.page.noPosts', label: 'No Posts Title', defaultValue: 'No Posts Yet' },
            { key: 'blog.page.noPostsDesc', label: 'No Posts Description', defaultValue: 'Check back soon for new travel stories and guides.' },
        ]
    },
    {
        id: 'contact_page',
        title: 'Contact Page',
        description: 'Content for the Contact Us page',
        group: 'Pages',
        items: [
            { key: 'contact.page.title', label: 'Page Title', defaultValue: 'Contact Us' },
            { key: 'contact.page.placeholder', label: 'Placeholder Text', defaultValue: 'Contact form coming soon...' },
        ]
    },
    {
        id: 'tour_details_page',
        title: 'Tour / Package Details',
        description: 'Labels for the tour and package detail pages',
        group: 'Pages',
        required: true,
        items: [
            // Error / not found
            { key: 'tour_details.not_found', label: 'Not Found Title', defaultValue: 'Tour Not Found' },
            { key: 'tour_details.not_found_desc', label: 'Not Found Description', defaultValue: 'The requested tour could not be loaded.' },
            { key: 'tour_details.return_to_tours', label: 'Return Link', defaultValue: 'Return to Tours' },
            // Language fallback
            { key: 'tour_details.not_available_in', label: 'Not Available In', defaultValue: 'Not Available in' },
            { key: 'tour_details.not_translated', label: 'Not Translated', defaultValue: 'are not translated into your current language yet.' },
            { key: 'tour_details.view_in', label: 'View In', defaultValue: 'You can view this tour in:' },
            { key: 'tour_details.go_back', label: 'Go Back', defaultValue: 'Go back' },
            // Tabs
            { key: 'tour_details.tab.description', label: 'Tab: Description', defaultValue: 'Description' },
            { key: 'tour_details.tab.itinerary', label: 'Tab: Itinerary', defaultValue: 'Itinerary' },
            { key: 'tour_details.tab.inclusions_exclusions', label: 'Tab: Included & Excluded', defaultValue: 'Included & Excluded' },
            { key: 'tour_details.tab.price', label: 'Tab: Price', defaultValue: 'Price' },
            { key: 'tour_details.tab.overview', label: 'Tab: Overview', defaultValue: 'Overview' },
            { key: 'tour_details.tab.amenities', label: 'Tab: Amenities', defaultValue: 'Amenities' },
            { key: 'tour_details.tab.location', label: 'Tab: Location', defaultValue: 'Location' },
            { key: 'tour_details.tab.calendar', label: 'Tab: Calendar', defaultValue: 'Calendar' },
            { key: 'tour_details.tab.reviews', label: 'Tab: Reviews', defaultValue: 'Reviews' },
            // Content sections
            { key: 'tour_details.description', label: 'Description Title', defaultValue: 'Description' },
            { key: 'tour_details.show_more', label: 'Show More', defaultValue: 'Show More' },
            { key: 'tour_details.show_less', label: 'Show Less', defaultValue: 'Show Less' },
            { key: 'tour_details.expand_all', label: 'Expand All', defaultValue: 'Expand All' },
            { key: 'tour_details.collapse_all', label: 'Collapse All', defaultValue: 'Collapse All' },
            { key: 'tour_details.itinerary_empty', label: 'Itinerary Empty', defaultValue: 'The itinerary schedule is currently not available.' },
            { key: 'tour_details.included', label: 'Included', defaultValue: 'Included' },
            { key: 'tour_details.excluded', label: 'Excluded', defaultValue: 'Excluded' },
            { key: 'tour_details.from', label: 'From Label', defaultValue: 'From' },
            { key: 'tour_details.reviews', label: 'Reviews Title', defaultValue: 'Reviews' },
            { key: 'tour_details.amenities', label: 'Amenities Title', defaultValue: 'Amenities' },
            { key: 'tour_details.amenity.meals', label: 'Amenity: Meals', defaultValue: 'Meals Included' },
            { key: 'tour_details.amenity.wifi', label: 'Amenity: WiFi', defaultValue: 'Free Wi-Fi' },
            { key: 'tour_details.amenity.photography', label: 'Amenity: Photography', defaultValue: 'Photography' },
            { key: 'tour_details.amenity.guide', label: 'Amenity: Guide', defaultValue: 'Expert Guide' },
            { key: 'tour_details.availability', label: 'Availability Title', defaultValue: 'Availability' },
            { key: 'tour_details.select_dates', label: 'Select Dates', defaultValue: 'Select Travel Dates' },
            { key: 'tour_details.free_cancellation', label: 'Free Cancellation', defaultValue: 'Free Cancellation' },
            { key: 'tour_details.cancellation_policy', label: 'Cancellation Policy', defaultValue: 'Cancel up to 24 hours before your trip and get a full refund.' },
            // Quick Info Cards
            { key: 'tour_details.quick_info.countries', label: 'Quick Info: Countries', defaultValue: 'Countries' },
            { key: 'tour_details.quick_info.cities', label: 'Quick Info: Cities', defaultValue: 'Cities' },
            { key: 'tour_details.quick_info.duration', label: 'Quick Info: Duration', defaultValue: 'Duration' },
            { key: 'tour_details.quick_info.tour_type', label: 'Quick Info: Tour Type', defaultValue: 'Tour Type' },
            // Booking sidebar
            { key: 'tour_details.per_person', label: 'Per Person', defaultValue: 'person' },
            { key: 'tour_details.best_price', label: 'Best Price', defaultValue: 'Best Price Guarantee' },
            { key: 'tour_details.no_charge', label: 'No Charge Yet', defaultValue: "You won't be charged yet" },
            // Breadcrumbs
            { key: 'tour_details.breadcrumb.home', label: 'Breadcrumb: Home', defaultValue: 'Home' },
            { key: 'tour_details.breadcrumb.packages', label: 'Breadcrumb: Packages', defaultValue: 'Packages' },
            { key: 'tour_details.breadcrumb.tours', label: 'Breadcrumb: Tours', defaultValue: 'Tours' },
            // Gallery
            { key: 'tour_details.view_all_photos', label: 'View All Photos', defaultValue: 'View All' },
            { key: 'tour_details.photos', label: 'Photos Label', defaultValue: 'Photos' },
            { key: 'tour_details.more', label: 'More Photos (+N more)', defaultValue: 'more' },
            // Reviews count
            { key: 'tour_details.reviews_count', label: 'Reviews Count Label', defaultValue: 'Reviews' },
            // Itinerary day label
            { key: 'tour_details.day', label: 'Itinerary Day Label', defaultValue: 'Day' },
            // Price table
            { key: 'tour_details.group_size', label: 'Price Table: Group Size', defaultValue: 'Group Size' },
            { key: 'tour_details.price_per_person', label: 'Price Table: Price / Person', defaultValue: 'Price / Person' },
            { key: 'tour_details.participants', label: 'Price Table: Participants', defaultValue: 'Participants' },
            { key: 'tour_details.selected', label: 'Price Table: Selected Badge', defaultValue: 'Selected' },
            // Inclusions empty states
            { key: 'tour_details.no_inclusions', label: 'No Inclusions', defaultValue: 'No inclusions listed' },
            { key: 'tour_details.no_exclusions', label: 'No Exclusions', defaultValue: 'No exclusions listed' },
            // Important notes
            { key: 'tour_details.important_notes', label: 'Important Notes', defaultValue: 'Important Notes' },
            // Related tours / You might also like
            { key: 'tour_details.related_tours', label: 'Related Tours Title', defaultValue: 'You Might Also Like' },
            { key: 'tour_details.booking_link', label: 'Booking Link Button', defaultValue: 'Booking' },
            // Mobile floating button
            { key: 'tour_details.inquire_now', label: 'Inquire Now Button', defaultValue: 'Inquire Now' },
            // Tour types
            { key: 'tour_details.type.package', label: 'Tour Type: Package', defaultValue: 'Package' },
            { key: 'tour_details.type.tour', label: 'Tour Type: Tour', defaultValue: 'Tour' },
        ]
    },
    {
        id: 'booking_common',
        title: 'Booking - Common',
        description: 'Shared booking labels used across booking pages',
        group: 'Common',
        required: true,
        items: [
            { key: 'booking.dates', label: 'Dates Label', defaultValue: 'Dates' },
            { key: 'booking.select_dates', label: 'Select Dates', defaultValue: 'Select dates' },
            { key: 'booking.guests', label: 'Guests Label', defaultValue: 'Guests' },
            { key: 'booking.guest', label: 'Guest (singular)', defaultValue: 'Guest' },
            { key: 'booking.total', label: 'Total', defaultValue: 'Total' },
            { key: 'booking.book_now', label: 'Book Now', defaultValue: 'Book Now' },
            { key: 'booking.for_1_day', label: 'For 1 Day', defaultValue: 'For 1 day' },
            { key: 'booking.full_name', label: 'Full Name', defaultValue: 'Full Name *' },
            { key: 'booking.email_address', label: 'Email Address', defaultValue: 'Email Address *' },
            { key: 'booking.nationality', label: 'Nationality', defaultValue: 'Nationality *' },
            { key: 'booking.mobile_number', label: 'Mobile Number', defaultValue: 'Mobile Number *' },
            { key: 'booking.start_date', label: 'Start Date', defaultValue: 'Start Date' },
            { key: 'booking.end_date', label: 'End Date', defaultValue: 'End Date' },
            { key: 'booking.adults', label: 'Adults', defaultValue: 'Adults (+12)' },
            { key: 'booking.children', label: 'Children', defaultValue: 'Child (2-11)' },
            { key: 'booking.infants', label: 'Infants', defaultValue: 'Infants (0-2)' },
            { key: 'booking.requirements', label: 'Requirements', defaultValue: 'Any specific requirements?' },
            { key: 'booking.payment_method_label', label: 'Payment Method Label', defaultValue: 'Payment Method' },
            { key: 'booking.recommended', label: 'Recommended Badge', defaultValue: 'Recommended' },
        ]
    },
    {
        id: 'booking_guest_details',
        title: 'Booking - Guest Details',
        description: 'Form labels for the guest details booking step',
        group: 'Pages',
        items: [
            { key: 'booking.details_title', label: 'Page Title', defaultValue: 'Guest Details' },
            { key: 'booking.details_subtitle', label: 'Page Subtitle', defaultValue: 'Who is travelling?' },
            { key: 'booking.contact_info', label: 'Contact Info', defaultValue: 'Contact Information' },
            { key: 'booking.email_label', label: 'Email Label', defaultValue: 'Email Address' },
            { key: 'booking.guest_details', label: 'Guest Details Title', defaultValue: 'Guest Details' },
            { key: 'booking.lead_guest', label: 'Lead Guest', defaultValue: 'Lead Guest' },
            { key: 'booking.first_name', label: 'First Name', defaultValue: 'First Name' },
            { key: 'booking.last_name', label: 'Last Name', defaultValue: 'Last Name' },
            { key: 'booking.phone', label: 'Phone', defaultValue: 'Phone Number' },
            { key: 'booking.special_requests', label: 'Special Requests', defaultValue: 'Special Requests' },
            { key: 'booking.optional', label: 'Optional', defaultValue: 'optional' },
            { key: 'booking.next_step', label: 'Next Step', defaultValue: 'Next Step' },
            { key: 'booking.fill_required', label: 'Fill Required', defaultValue: 'Please fill in all required guest information' },
            // OTP
            { key: 'booking.otp_message', label: 'OTP Message', defaultValue: 'Welcome back! We sent a code to your email. Please verify to continue.' },
            { key: 'booking.verify', label: 'Verify', defaultValue: 'Verify' },
            { key: 'booking.invalid_otp', label: 'Invalid OTP', defaultValue: 'Invalid verification code' },
            { key: 'booking.please_verify', label: 'Please Verify', defaultValue: 'Please verify your email to continue' },
            { key: 'booking.change_email', label: 'Change Email', defaultValue: 'Use a different email' },
            { key: 'booking.resend_otp', label: 'Resend OTP', defaultValue: 'Resend' },
        ]
    },
    {
        id: 'booking_payment',
        title: 'Booking - Payment',
        description: 'Form labels for the payment booking step',
        group: 'Pages',
        items: [
            { key: 'booking.payment_title', label: 'Page Title', defaultValue: 'Payment' },
            { key: 'booking.payment_subtitle', label: 'Page Subtitle', defaultValue: 'Finish booking' },
            { key: 'booking.payment_method', label: 'Payment Method', defaultValue: 'Payment Method' },
            { key: 'booking.pay_on_arrival', label: 'Cash on Arrival', defaultValue: 'Cash on arrival' },
            { key: 'booking.pay_with_card', label: 'Pay with Card', defaultValue: 'Credit / Debit Card' },
            { key: 'booking.card_details', label: 'Card Details', defaultValue: 'Card Details' },
            { key: 'booking.cardholder_name', label: 'Cardholder Name', defaultValue: "Cardholder's name" },
            { key: 'booking.card_number', label: 'Card Number', defaultValue: 'Card number' },
            { key: 'booking.expiry', label: 'Expiry', defaultValue: 'Expiry' },
            { key: 'booking.confirm_pay', label: 'Confirm Button', defaultValue: 'Confirm Booking' },
            { key: 'booking.fill_card_details', label: 'Fill Card Error', defaultValue: 'Please fill in all card details' },
            { key: 'booking.missing_info', label: 'Missing Info Error', defaultValue: 'Missing booking information. Please start over.' },
            { key: 'booking.failed', label: 'Booking Failed', defaultValue: 'Booking failed. Please try again.' },
        ]
    },
    {
        id: 'booking_confirmation',
        title: 'Booking - Confirmation',
        description: 'Labels for the booking confirmation page',
        group: 'Pages',
        items: [
            { key: 'booking.confirmed', label: 'Confirmed Title', defaultValue: 'Booking Confirmed' },
            { key: 'booking.ready', label: 'Ready Subtitle', defaultValue: 'Get ready for your trip!' },
            { key: 'booking.success_message', label: 'Success Message', defaultValue: "You're all set!" },
            { key: 'booking.confirmation_text', label: 'Confirmation Text', defaultValue: "We've sent your itinerary to your email." },
            { key: 'booking.code', label: 'Code Label', defaultValue: 'Code' },
            { key: 'booking.email_itinerary', label: 'Email Itinerary', defaultValue: 'Email your itinerary' },
            { key: 'booking.send', label: 'Send', defaultValue: 'Send' },
            { key: 'booking.sent', label: 'Sent', defaultValue: 'Sent' },
            { key: 'booking.download_invoice', label: 'Download Invoice', defaultValue: 'Invoice' },
            { key: 'booking.back_home', label: 'Back Home', defaultValue: 'Back to Home' },
            { key: 'booking.pending_approval', label: 'Pending Approval', defaultValue: 'Your booking is pending approval. You will receive a confirmation email shortly.' },
        ]
    },
    {
        id: 'tour_card',
        title: 'Tour Card',
        description: 'Labels for tour cards',
        group: 'Common',
        items: [
            { key: 'tour_card.perPerson', label: 'Per Person', defaultValue: '/ person' },
            { key: 'tour_card.bookNow', label: 'Book Now', defaultValue: 'Book Now' },
        ]
    },
    {
        id: 'dynamic_filters',
        title: 'Dynamic Filters',
        description: 'Labels for the dynamic filters component',
        group: 'Common',
        items: [
            { key: 'filters.filterBy', label: 'Filter By Title', defaultValue: 'Filter by:' },
            { key: 'filters.clear', label: 'Clear Button', defaultValue: 'Clear' },
            { key: 'filters.anyType', label: 'Any Type Option', defaultValue: 'Any type' },
        ]
    },
    {
        id: 'package_card',
        title: 'Package Card',
        description: 'Labels for package list cards',
        group: 'Common',
        items: [
            { key: 'package_card.excellent', label: 'Rating Label', defaultValue: 'Excellent' },
            { key: 'package_card.reviews', label: 'Reviews Label', defaultValue: 'reviews' },
            { key: 'package_card.fromCentre', label: 'Distance from Centre', defaultValue: 'from centre' },
            { key: 'package_card.metroAccess', label: 'Metro Access', defaultValue: 'Metro access' },
            { key: 'package_card.fromBeach', label: 'Distance from Beach', defaultValue: 'from beach' },
            { key: 'package_card.nights', label: 'Nights Label', defaultValue: 'nights' },
            { key: 'package_card.adults', label: 'Adults Label', defaultValue: 'adults' },
            { key: 'package_card.off', label: 'Discount Label', defaultValue: 'off' },
            { key: 'package_card.freeCancellation', label: 'Amenity: Free Cancellation', defaultValue: 'Free cancellation' },
            { key: 'package_card.spaAccess', label: 'Amenity: Spa Access', defaultValue: 'Spa access' },
            { key: 'package_card.breakfastIncluded', label: 'Amenity: Breakfast Included', defaultValue: 'Breakfast included' },
        ]
    },
    {
        id: 'listing_common',
        title: 'Listing Pages (Common)',
        description: 'Shared labels for Tours, Packages, and Explore pages',
        group: 'Common',
        items: [
            { key: 'listing.tabs.all', label: 'Tab: All', defaultValue: 'All' },
            { key: 'listing.tabs.packages', label: 'Tab: Packages', defaultValue: 'Packages' },
            { key: 'listing.tabs.tours', label: 'Tab: Tours', defaultValue: 'Tours' },
            { key: 'listing.sort.topReviewed', label: 'Sort: Top Reviewed', defaultValue: 'Top Reviewed' },
            { key: 'listing.sort.priceLowToHigh', label: 'Sort: Price Low-High', defaultValue: 'Price: Low to High' },
            { key: 'listing.sort.priceHighToLow', label: 'Sort: Price High-Low', defaultValue: 'Price: High to Low' },
            { key: 'listing.sort.rating', label: 'Sort: Rating', defaultValue: 'Rating' },
            { key: 'listing.map.show', label: 'Show Map', defaultValue: 'Show Map' },
            { key: 'listing.filters.title', label: 'Filters Title', defaultValue: 'Filters' },
            { key: 'listing.filters.applied', label: 'Filters Applied', defaultValue: 'filters applied' },
            { key: 'listing.sortBy', label: 'Sort By Label', defaultValue: 'Sort by' },
            { key: 'listing.noResults', label: 'No Results', defaultValue: 'No results found' },
            { key: 'listing.error', label: 'Error Message', defaultValue: 'Failed to load items' },
        ]
    },
    {
        id: 'datepicker_component',
        title: 'Date Picker',
        description: 'Labels for the date picker component',
        group: 'Common',
        items: [
            { key: 'datepicker.select_dates', label: 'Select Dates', defaultValue: 'Select dates' },
            { key: 'datepicker.select_checkout', label: 'Select Checkout', defaultValue: 'Select checkout' },
        ]
    },
    {
        id: 'reviews_comments',
        title: 'Reviews & Comments',
        description: 'Labels for review and comment sections on tour/blog pages',
        group: 'Common',
        required: true,
        items: [
            // ReviewsTab
            { key: 'reviews.loading', label: 'Loading Reviews', defaultValue: 'Loading reviews...' },
            { key: 'reviews.title', label: 'Reviews Section Title', defaultValue: 'Guest Reviews' },
            { key: 'reviews.subtitle', label: 'Reviews Section Subtitle', defaultValue: 'See what other travelers are saying about this tour.' },
            { key: 'reviews.count', label: 'Reviews Count Label', defaultValue: 'reviews' },
            { key: 'reviews.no_reviews', label: 'No Reviews Text', defaultValue: 'No reviews yet. Be the first to review this tour!' },
            { key: 'reviews.write_title', label: 'Write a Review Title', defaultValue: 'Write a Review' },
            { key: 'reviews.rating_label', label: 'Rating Label', defaultValue: 'Rating' },
            { key: 'reviews.review_label', label: 'Review Text Label', defaultValue: 'Review' },
            { key: 'reviews.review_placeholder', label: 'Review Placeholder', defaultValue: 'Share your experience...' },
            { key: 'reviews.submit', label: 'Submit Button', defaultValue: 'Submit Review' },
            { key: 'reviews.submitting', label: 'Submitting State', defaultValue: 'Sending...' },
            { key: 'reviews.submitted_title', label: 'Review Submitted Title', defaultValue: 'Review Submitted!' },
            { key: 'reviews.submitted_text', label: 'Review Submitted Message', defaultValue: 'Thank you for your feedback. Your review is pending approval and will be published shortly.' },
            { key: 'reviews.write_another', label: 'Write Another Button', defaultValue: 'Write another review' },
            { key: 'reviews.submit_error', label: 'Submit Error', defaultValue: 'Failed to submit review. Please try again.' },
            { key: 'reviews.login_title', label: 'Login Required Title', defaultValue: 'Please log in to write a review' },
            { key: 'reviews.login_text', label: 'Login Required Text', defaultValue: 'You need to be signed in to your account to post a review.' },
            { key: 'reviews.login_link', label: 'Log In Link', defaultValue: 'Log In' },
            { key: 'reviews.signup_link', label: 'Sign Up Link', defaultValue: 'Sign Up' },
            // CommentsSection
            { key: 'comments.title', label: 'Comments Title', defaultValue: 'Comments' },
            { key: 'comments.no_comments', label: 'No Comments Text', defaultValue: 'No comments yet. Be the first to share your thoughts!' },
            { key: 'comments.write_title', label: 'Write Comment Title', defaultValue: 'Leave a Comment' },
            { key: 'comments.write_reply_title', label: 'Write Reply Title', defaultValue: 'Write a Reply' },
            { key: 'comments.name_label', label: 'Name Label', defaultValue: 'Name *' },
            { key: 'comments.email_label', label: 'Email Label', defaultValue: 'Email *' },
            { key: 'comments.comment_label', label: 'Comment Label', defaultValue: 'Comment *' },
            { key: 'comments.comment_placeholder', label: 'Comment Placeholder', defaultValue: 'Share your thoughts...' },
            { key: 'comments.privacy_note', label: 'Privacy Note', defaultValue: 'Your email address will not be published.' },
            { key: 'comments.submit', label: 'Submit Button', defaultValue: 'Post Comment' },
            { key: 'comments.submit_reply', label: 'Submit Reply Button', defaultValue: 'Post Reply' },
            { key: 'comments.submitting', label: 'Submitting State', defaultValue: 'Submitting...' },
            { key: 'comments.reply', label: 'Reply Button', defaultValue: 'Reply' },
            { key: 'comments.cancel_reply', label: 'Cancel Reply Button', defaultValue: 'Cancel Reply' },
        ]
    }
];
